// L-action (docs/design/states-pass.md): a button that writes says what it is doing ("Booking…"),
// keeps its width, is disabled and can't be pressed twice; when it fails its label comes back with
// one line under it that says why. useSaveKey gives the write one idempotency key per attempt at the
// same thing, so pressing it again after a lost answer can't do it twice (QA-058).

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { newId } from './ids.ts';

export { newId };
// the key lives on its own (save-key.ts), so a form drawn with ui-web's Button needn't load this sheet
export { useSaveKey } from './save-key.ts';
import './save-button.css';

export function SaveButton({
  busy,
  busyLabel,
  error,
  errorId,
  disabled,
  className = 'btn primary',
  onClick,
  children,
}: {
  busy: boolean;
  /** The -ing form, e.g. "Booking…". */
  busyLabel: string;
  /** Why the last try failed; shown under the button until the next one. */
  error?: string | null;
  /** The why line's id, for a field it names to point at (aria-describedby). */
  errorId?: string;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  // Wide enough for either label from the start, so swapping to "Sending back…" never resizes it.
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const b = ref.current;
    if (!b || busy) return;
    const cs = getComputedStyle(b);
    const ctx = document.createElement('canvas').getContext('2d');
    if (ctx) ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) * 2;
    const need = Math.ceil((ctx?.measureText(busyLabel).width ?? 0) + pad);
    const w = Math.max(b.offsetWidth, need);
    if (w !== width && b.style.minWidth !== `${w}px`) setWidth(w);
  });
  return (
    <span className="save">
      <button
        ref={ref}
        type="button"
        className={className}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        style={width ? { minWidth: width } : undefined}
        onClick={() => {
          if (!busy) onClick();
        }}
      >
        {busy ? busyLabel : children}
      </button>
      {error && !busy && (
        <span className="save-why" role="alert" {...(errorId ? { id: errorId } : {})}>
          {error}
        </span>
      )}
    </span>
  );
}
